import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../services/api';
import EmployerLayout from '../../layouts/EmployerLayout';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import toast from 'react-hot-toast';

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

function InfoRow({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      <p className="text-sm font-medium text-gray-800">{value || '—'}</p>
    </div>
  );
}

export default function EmployerRequirementDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    api.get(`/requirements/${id}`)
      .then((r) => setReq(r.data.data))
      .catch(() => navigate('/employer/requirements'))
      .finally(() => setLoading(false));
  }, [id]);

  async function submitForApproval() {
    setSubmitting(true);
    try {
      await api.post(`/requirements/${id}/submit`);
      toast.success('Submitted for admin approval');
      setReq((p) => ({ ...p, status: 'pending_approval' }));
    } catch {}
    finally { setSubmitting(false); }
  }

  async function updateStatus(status) {
    setClosing(true);
    try {
      await api.patch(`/requirements/${id}/status`, { status });
      toast.success(`Requirement ${status}`);
      setReq((p) => ({ ...p, status }));
    } catch {}
    finally { setClosing(false); }
  }

  if (loading) return (
    <EmployerLayout>
      <div className="flex justify-center py-20">
        <div className="animate-spin h-8 w-8 border-4 border-india border-t-transparent rounded-full" />
      </div>
    </EmployerLayout>
  );

  if (!req) return null;

  return (
    <EmployerLayout>
      <div className="max-w-3xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <button onClick={() => navigate('/employer/requirements')} className="text-sm text-gray-400 hover:text-gray-600 mb-2 flex items-center gap-1">
              ← Back to Requirements
            </button>
            <h1 className="text-xl font-bold text-gray-900">{req.title}</h1>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Badge label={req.status} variant={req.status} />
            <Badge label={req.urgency} variant={req.urgency} />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{req.applications_count || 0}</p>
            <p className="text-xs text-gray-500 mt-0.5">Applications</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{req.number_of_workers}</p>
            <p className="text-xs text-gray-500 mt-0.5">Workers Needed</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{req.views_count || 0}</p>
            <p className="text-xs text-gray-500 mt-0.5">Views</p>
          </div>
        </div>

        {/* Actions */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap gap-3">
          {req.status === 'draft' && (
            <Button loading={submitting} onClick={submitForApproval}>Submit for Approval</Button>
          )}
          {req.status === 'active' && (
            <>
              <Link to={`/employer/requirements/${id}/applicants`}>
                <Button>View Applicants ({req.applications_count || 0})</Button>
              </Link>
              <Button variant="secondary" loading={closing} onClick={() => updateStatus('paused')}>Pause</Button>
              <Button variant="danger" loading={closing} onClick={() => updateStatus('closed')}>Close</Button>
            </>
          )}
          {req.status === 'paused' && (
            <>
              <Link to={`/employer/requirements/${id}/applicants`}>
                <Button variant="secondary">View Applicants</Button>
              </Link>
              <Button loading={closing} onClick={() => updateStatus('active')}>Re-activate</Button>
              <Button variant="danger" loading={closing} onClick={() => updateStatus('closed')}>Close</Button>
            </>
          )}
          {(req.status === 'closed' || req.status === 'expired') && (
            <Link to={`/employer/requirements/${id}/applicants`}>
              <Button variant="secondary">View Applicants</Button>
            </Link>
          )}
        </div>

        {/* Details */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-800 mb-4 border-b border-gray-100 pb-2">Job Details</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <InfoRow label="Category" value={req.category_name} />
            <InfoRow label="Location" value={[req.location_text, req.district, req.state].filter(Boolean).join(', ')} />
            <InfoRow label="Start Date" value={fmt(req.start_date)} />
            <InfoRow label="End Date" value={fmt(req.end_date)} />
            <InfoRow label="Apply By" value={fmt(req.application_deadline)} />
            <InfoRow label="Shift" value={req.shift_hours ? `${req.shift_hours} hrs/day` : null} />
            <InfoRow label="Wage" value={req.wage_min ? `₹${req.wage_min}${req.wage_max ? `–${req.wage_max}` : '+'} / ${req.wage_type}` : null} />
            <InfoRow label="Gender Preference" value={req.gender_preference || 'Any'} />
            <InfoRow label="Age Range" value={req.min_age || req.max_age ? `${req.min_age || 18}–${req.max_age || 60} yrs` : null} />
            <InfoRow label="Contact Person" value={req.contact_person} />
            <InfoRow label="Contact Mobile" value={req.contact_mobile} />
          </div>

          <div className="flex gap-3 mt-4 pt-3 border-t border-gray-100">
            {req.accommodation   && <span className="text-xs bg-blue-50 text-blue-700 px-3 py-1 rounded-full">🏠 Accommodation</span>}
            {req.food_provided   && <span className="text-xs bg-green-50 text-green-700 px-3 py-1 rounded-full">🍽️ Food</span>}
            {req.travel_provided && <span className="text-xs bg-purple-50 text-purple-700 px-3 py-1 rounded-full">🚌 Travel</span>}
          </div>
        </div>

        {/* Skills */}
        {req.skills?.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="font-semibold text-gray-800 mb-3">Required Skills</h2>
            <div className="flex flex-wrap gap-2">
              {req.skills.map((s) => (
                <span key={s.skill_id}
                  className={`text-xs px-3 py-1 rounded-full ${s.is_mandatory ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-600 border'}`}>
                  {s.skill_name}{s.is_mandatory ? ' *' : ''}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Description */}
        {req.description && (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="font-semibold text-gray-800 mb-2">Description</h2>
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{req.description}</p>
          </div>
        )}
      </div>
    </EmployerLayout>
  );
}
